<template>
  <aside class="controls-card" :class="{ open }" aria-label="Controls">
    <button type="button" class="toggle" @click="toggle">
      Controls <span aria-hidden="true">{{ open ? '▾' : '▴' }}</span>
    </button>
    <div v-if="open" class="body">
      <section v-for="group in groups" :key="group.kind" class="group">
        <p class="who">
          <span v-for="p in group.players" :key="p.name" :style="{ color: p.fill }">{{ p.name }}</span>
        </p>

        <!-- Controller: a plain drawing for "where", words for "what". -->
        <template v-if="group.kind === 'pad'">
          <svg viewBox="0 0 260 110" class="diagram" aria-hidden="true">
            <path class="body-shape" d="M40 20 Q60 8 95 12 L165 12 Q200 8 220 20 Q250 40 252 82 Q254 106 232 108 Q214 110 200 88 L182 68 L78 68 L60 88 Q46 110 28 108 Q6 106 8 82 Q10 40 40 20Z" />
            <rect x="52" y="30" width="16" height="44" rx="3" class="key" />
            <rect x="38" y="44" width="44" height="16" rx="3" class="key" />
            <rect x="160" y="26" width="12" height="7" rx="3.5" class="key" />
            <g class="face">
              <circle cx="202" cy="32" r="10" /><text x="202" y="36" class="g">△</text>
              <circle cx="202" cy="72" r="10" /><text x="202" y="76" class="g">✕</text>
              <circle cx="182" cy="52" r="10" /><text x="182" y="56" class="g">□</text>
              <circle cx="222" cy="52" r="10" /><text x="222" y="56" class="g">○</text>
            </g>
          </svg>
          <ul>
            <li><b>D-pad ◀ ▶</b> move · <b>▼</b> a row · <b>▲</b> drop</li>
            <li><b>✕ △</b> (A Y) turn ↺ · <b>□ ○</b> (X B) turn ↻</li>
            <li><b>Options</b> pause</li>
          </ul>
        </template>

        <!-- Keyboard: both clusters work, whichever hand. -->
        <div v-else-if="group.kind === 'keys'" class="keys">
          <div class="cluster">
            <div class="row"><kbd>W</kbd></div>
            <div class="row"><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd></div>
          </div>
          <span class="or">or</span>
          <div class="cluster">
            <div class="row"><kbd>↑</kbd></div>
            <div class="row"><kbd>←</kbd><kbd>↓</kbd><kbd>→</kbd></div>
          </div>
          <ul>
            <li><b>A D / ← →</b> move</li>
            <li><b>W / ↑</b> turn · <b>Q /</b> turn back</li>
            <li><b>S / ↓</b> a row · <b>Space</b> drop</li>
            <li><b>Esc</b> pause</li>
          </ul>
        </div>

        <!-- Phones (party mode). -->
        <ul v-else-if="group.kind === 'phone'" class="phone">
          <li><b>Drag</b> sideways to move</li>
          <li><b>Tap</b> to turn</li>
          <li><b>Flick down</b> to drop</li>
          <li><b>Drag down slowly</b> for a row at a time</li>
        </ul>
      </section>
      <p class="foot">Slide into someone to shove them. Falling never pushes.</p>
    </div>
  </aside>
</template>

<script>
const OPEN_KEY = 'couchBlocks:controlsOpen';

// What the people at THIS computer are holding, drawn in a corner of the
// game screen. Matt: "a diagram on the screen that shows the controls while
// the game is on ... out of the way but discoverable ... related to the
// players connected to that computer."
export default {
  name: 'ControlsCard',
  props: {
    // [{ kind: 'pad' | 'keys' | 'phone', name, fill }]
    players: { type: Array, required: true },
    // False when the well is too wide for the card to sit beside it: then
    // it starts folded to its tab, so it never covers the game unasked.
    fits: { type: Boolean, default: true },
  },
  data() {
    let saved = true;
    try { saved = window.localStorage.getItem(OPEN_KEY) !== 'closed'; } catch { /* default open */ }
    return { open: saved && this.fits };
  },
  computed: {
    groups() {
      const order = ['pad', 'keys', 'phone'];
      return order
        .map((kind) => ({ kind, players: this.players.filter((p) => p.kind === kind) }))
        .filter((g) => g.players.length);
    },
  },
  methods: {
    toggle() {
      this.open = !this.open;
      try { window.localStorage.setItem(OPEN_KEY, this.open ? 'open' : 'closed'); } catch { /* fine */ }
      document.activeElement?.blur?.();
    },
  },
};
</script>

<style scoped>
.controls-card {
  position: fixed; right: 0.75rem; bottom: calc(0.75rem + var(--safe-bottom, 0px)); z-index: 4;
  width: 250px; color: #eef0f6; font-size: 0.8rem;
  background: rgba(26, 29, 41, 0.72); border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px; opacity: 0.6; transition: opacity 0.2s;
}
.controls-card:hover { opacity: 1; }
.controls-card:not(.open) { width: auto; }
.toggle {
  display: block; width: 100%; background: none; border: 0; color: inherit; font: inherit;
  font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; text-align: left;
  padding: 0.45rem 0.7rem;
}
.body { padding: 0 0.7rem 0.6rem; }
.group + .group { border-top: 1px solid rgba(255, 255, 255, 0.08); margin-top: 0.4rem; padding-top: 0.4rem; }
.who { margin: 0 0 0.25rem; display: flex; flex-wrap: wrap; gap: 0.15rem 0.5rem; font-weight: 800; }
.diagram { width: 70%; height: auto; display: block; margin: 0.1rem auto 0.2rem; }
.body-shape { fill: rgba(255, 255, 255, 0.06); stroke: rgba(255, 255, 255, 0.35); stroke-width: 1.5; }
.key { fill: rgba(255, 255, 255, 0.28); }
.face circle { fill: rgba(255, 255, 255, 0.12); stroke: rgba(255, 255, 255, 0.4); }
.g { fill: #eef0f6; font-size: 12px; text-anchor: middle; }
.keys { display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; }
.cluster .row { display: flex; justify-content: center; gap: 2px; margin-top: 2px; }
kbd {
  display: inline-grid; place-items: center; min-width: 20px; height: 20px; padding: 0 3px;
  border: 1px solid rgba(255, 255, 255, 0.35); border-bottom-width: 2px; border-radius: 4px;
  font: 700 0.7rem system-ui, sans-serif; color: #eef0f6;
}
.or { opacity: 0.6; }
ul { list-style: none; margin: 0.3rem 0 0; padding: 0; width: 100%; }
li { margin: 0.12rem 0; }
li b { color: #fff; }
.foot { margin: 0.5rem 0 0; opacity: 0.6; }
</style>
